import { circleFromDiameter } from '../shared/circle-geometry.js';
import { projectPointToSegment } from '../shared/planar-geometry.js';
import { haversineKm } from './distance-math.js';
import { linePathPoints } from './line-geometry.js';
import type { Stroke } from '../types/app.js';
import { hasPlatformModifier, isMacControlClick } from './browser-helpers.js';
import { getGlobeProjection } from './globe-projection.js';
import { isNumber } from '../shared/runtime-values.js';
import { fromLonLat, toLonLat } from 'ol/proj.js';
import type { AppStateContract } from '../types/app.js';

interface LatLng {
  lat: number;
  lng: number;
}

interface ScreenPoint {
  x: number;
  y: number;
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface StrokeLike {
  width: number;
  zoomScale?: boolean;
  zoom?: number;
  points: LatLng[];
  tool?: string;
  markerLabelOffset?: number;
}

const DEFAULT_ARROW_WIDTH = 4;
const DEFAULT_ARROW_HEAD_LENGTH = 15;
const ARROW_HEAD_LENGTH_RATIO = DEFAULT_ARROW_HEAD_LENGTH / DEFAULT_ARROW_WIDTH;

export function getCoordinateForLatLng(latLng: LatLng): number[] {
  return fromLonLat([latLng.lng, latLng.lat]);
}

export function toLatLng(coordinate: number[]): LatLng {
  const [lng, lat] = toLonLat(coordinate);
  return { lat, lng };
}

export function getMapCenter(state: AppStateContract): LatLng {
  const center = state.olView.getCenter() || [0, 0];
  return toLatLng(center);
}

export function setMapView(state: AppStateContract, latLng: LatLng, zoom: number = getCurrentZoom(state)): void {
  state.olView.setCenter(getCoordinateForLatLng(latLng));
  state.olView.setZoom(zoom);
}

export function getCurrentZoom(state: { olView?: { getZoom(): number | undefined } | null }): number {
  const zoom = getGlobeProjection()?.zoom() ?? state.olView?.getZoom();
  return isNumber(zoom) ? zoom : 13;
}

export function getCurrentBounds(state: AppStateContract) {
  const size = state.olMap.getSize();
  const extent = size ? state.olView.calculateExtent(size) : null;
  if (!extent) {
    return {
      getNorthWest() { return getMapCenter(state); },
      getSouthEast() { return getMapCenter(state); }
    };
  }
  const southWest = toLatLng([extent[0], extent[1]]);
  const northEast = toLatLng([extent[2], extent[3]]);
  return {
    getNorthWest() { return { lat: northEast.lat, lng: southWest.lng }; },
    getSouthEast() { return { lat: southWest.lat, lng: northEast.lng }; }
  };
}

export function getZoomScale(targetZoom: number, baseZoom: number): number {
  return Math.pow(2, targetZoom - baseZoom);
}

export function eventToContainerPoint(state: AppStateContract, e: { clientX: number; clientY: number }): ScreenPoint {
  const rect = state.mapElement.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

export function screenToLatLng(state: AppStateContract, x: number, y: number): LatLng | null {
  const globe = getGlobeProjection();
  if (globe) return globe.unproject({ x, y });
  const coordinate = state.olMap.getCoordinateFromPixel([x, y]);
  if (!coordinate) return null;
  return toLatLng(coordinate);
}

export function screenPointToLatLng(state: AppStateContract, point: ScreenPoint): LatLng | null {
  return screenToLatLng(state, point.x, point.y);
}

export function latLngToScreen(state: AppStateContract, lat: number, lng: number): ScreenPoint {
  const globe = getGlobeProjection();
  if (globe) return globe.project({ lat, lng }) ?? { x: NaN, y: NaN };
  const center = state.olView.getCenter();
  if (center) {
    const [centerLng] = toLonLat(center);
    while (lng - centerLng > 180) lng -= 360;
    while (lng - centerLng < -180) lng += 360;
  }
  const point = state.olMap.getPixelFromCoordinate(getCoordinateForLatLng({ lat, lng }));
  if (!point) return { x: 0, y: 0 };
  return { x: point[0], y: point[1] };
}

export function distanceBetweenPoints(a: ScreenPoint, b: ScreenPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function pointToSegmentDistanceSq(point: ScreenPoint, segmentStart: ScreenPoint, segmentEnd: ScreenPoint): number {
  return projectPointToSegment(point, segmentStart, segmentEnd).distanceSq;
}

export function simplifyFreehandPointsRdpPixelSpace(state: AppStateContract, points: LatLng[], tolerancePx: number): LatLng[] {
  if (!Array.isArray(points) || points.length <= 2) return points ? points.slice() : [];
  const projectedPoints = points.map((pt) => latLngToScreen(state, pt.lat, pt.lng));
  const keep = new Uint8Array(points.length);
  const lastIndex = points.length - 1;
  keep[0] = 1;
  keep[lastIndex] = 1;
  const toleranceSq = tolerancePx * tolerancePx;
  const stack: Array<[number, number]> = [[0, lastIndex]];
  while (stack.length > 0) {
    const pair = stack.pop();
    if (!pair) continue;
    const [startIdx, endIdx] = pair;
    if (endIdx - startIdx <= 1) continue;
    const segmentStart = projectedPoints[startIdx];
    const segmentEnd = projectedPoints[endIdx];
    let furthestIdx = -1;
    let maxDistanceSq = -1;
    for (let i = startIdx + 1; i < endIdx; i++) {
      const dsq = pointToSegmentDistanceSq(projectedPoints[i], segmentStart, segmentEnd);
      if (dsq > maxDistanceSq) {
        maxDistanceSq = dsq;
        furthestIdx = i;
      }
    }
    if (furthestIdx !== -1 && maxDistanceSq > toleranceSq) {
      keep[furthestIdx] = 1;
      stack.push([startIdx, furthestIdx], [furthestIdx, endIdx]);
    }
  }
  const simplified: LatLng[] = [];
  for (let i = 0; i < points.length; i++) {
    if (keep[i]) simplified.push(points[i]);
  }
  if (simplified.length >= 2) return simplified;
  return [points[0], points[lastIndex]];
}

export function getRenderedWidth(stroke: StrokeLike, currentZoom: number): number {
  const zoomScale = stroke.zoomScale !== false;
  if (!zoomScale) return Math.max(0.5, stroke.width);
  const baseZoom = isNumber(stroke.zoom) ? stroke.zoom : currentZoom;
  return Math.max(0.5, stroke.width * getZoomScale(currentZoom, baseZoom));
}

export function getArrowHeadLength(renderedWidth: number): number {
  return Math.max(0.5, renderedWidth) * ARROW_HEAD_LENGTH_RATIO;
}

export function getRenderedTextFontSize(stroke: StrokeLike, currentZoom: number): number {
  const base = Math.max(8, stroke.width * 5);
  if (stroke.zoomScale === false) return base;
  const baseZoom = isNumber(stroke.zoom) ? stroke.zoom : currentZoom;
  return Math.max(0.5, base * getZoomScale(currentZoom, baseZoom));
}

/** Whether a text stroke is a marker-generated label (rendered below and centered on its icon). */
export function isMarkerLabelStroke(stroke: StrokeLike): boolean {
  return stroke.markerLabelOffset !== undefined;
}

/** Zoom-scaled screen-space vertical offset that keeps a marker-generated label below its icon anchor. */
export function getRenderedMarkerLabelOffset(stroke: StrokeLike, currentZoom: number): number {
  const markerLabelOffset = stroke.markerLabelOffset;
  if (markerLabelOffset === undefined) return 0;
  if (stroke.zoomScale === false) return markerLabelOffset;
  const baseZoom = isNumber(stroke.zoom) ? stroke.zoom : currentZoom;
  return markerLabelOffset * getZoomScale(currentZoom, baseZoom);
}

export function strokeTotalDistanceKm(pts: LatLng[]): number {
  let total = 0;
  for (let index = 1; index < pts.length; index++) total += haversineKm(pts[index - 1].lat, pts[index - 1].lng, pts[index].lat, pts[index].lng);
  return total;
}

export function strokeDistanceKm(stroke: Pick<Stroke, 'tool' | 'points' | 'curveControls'>): number {
  return strokeTotalDistanceKm(linePathPoints(stroke));
}

export function formatStrokeDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(2)} km`;
  return `${Math.round(km)} km`;
}

export function getCircleRadiusKm(stroke: StrokeLike): number {
  return circleFromDiameter(stroke.points).radiusKm;
}

export function hasAdditiveSelectionModifier(event: { shiftKey?: boolean; metaKey?: boolean; ctrlKey?: boolean }): boolean {
  return !isMacControlClick(event) && !!(event.shiftKey || hasPlatformModifier(event));
}

export function getSelectionBoundsFromPoints(points: ScreenPoint[]): Bounds | null {
  if (!points.length || points.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))) return null;
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { minX, minY, maxX, maxY };
}

export function expandBounds(bounds: Bounds, padding: number): Bounds {
  return {
    minX: bounds.minX - padding,
    minY: bounds.minY - padding,
    maxX: bounds.maxX + padding,
    maxY: bounds.maxY + padding
  };
}

export function pointInBounds(point: ScreenPoint, bounds: Bounds): boolean {
  return point.x >= bounds.minX && point.x <= bounds.maxX && point.y >= bounds.minY && point.y <= bounds.maxY;
}

export function boundsIntersect(a: Bounds, b: Bounds): boolean {
  return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
}

export function getSelectionCenter(bounds: Bounds): ScreenPoint {
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2
  };
}

export function rotatePointAroundCenter(point: ScreenPoint, center: ScreenPoint, angle: number): ScreenPoint {
  const offsetX = point.x - center.x;
  const offsetY = point.y - center.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: center.x + offsetX * cos - offsetY * sin,
    y: center.y + offsetX * sin + offsetY * cos
  };
}
